import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import type { Identifier, XYCoord } from "dnd-core";

const PANEL_TYPE = "DASHBOARD_PANEL";
type DragPosition = "left" | "right" | "top" | "bottom";

interface DraggablePanelProps {
  id: string;
  index: number;
  children: React.ReactNode;
  movePanelTo: (
    dragIndex: number,
    hoverIndex: number,
    position: DragPosition,
    dragId: string,
    hoverId: string
  ) => number;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

const resolveDragPosition = (deltaX: number, deltaY: number): DragPosition => {
  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    return deltaX < 0 ? "left" : "right";
  }
  return deltaY < 0 ? "top" : "bottom";
};

export default function DraggablePanel({ id, index, children, movePanelTo }: Readonly<DraggablePanelProps>) {
  const ref = useRef<HTMLDivElement>(null);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: PANEL_TYPE,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }

      const pointer = clientOffset as XYCoord;
      const lastPointer = lastPointerRef.current;
      lastPointerRef.current = { x: pointer.x, y: pointer.y };
      if (!lastPointer) {
        return;
      }

      const deltaX = pointer.x - lastPointer.x;
      const deltaY = pointer.y - lastPointer.y;
      if (deltaX === 0 && deltaY === 0) {
        return;
      }

      const position = resolveDragPosition(deltaX, deltaY);

      const nextIndex = movePanelTo(dragIndex, hoverIndex, position, item.id, id);
      item.index = nextIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: PANEL_TYPE,
    item: () => {
      return { id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const dragHandleRef = useRef<HTMLButtonElement>(null);

  preview(drop(ref));
  drag(dragHandleRef);

  return (
    <div ref={ref} data-handler-id={handlerId} className={`position-relative ${isDragging ? "opacity-50" : ""}`}>
      <button
        type="button"
        ref={dragHandleRef}
        title="Arrastar para reordenar"
        style={{ cursor: isDragging ? "grabbing" : "move" }}
        className="btn btn-link btn-icon-only position-absolute top-20 right-10 text-size-20 text-color-light user-select-none display-none display-inline-block-sm"
      >
        <span className="rioglyph rioglyph-drag-n-drop text-color-light" />
      </button>
      {children}
    </div>
  );
}
